import type { AiAction } from '~/components/AiChat/types';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';

import { activityRowKey, getNotifiedActionId, getNotifiedActionState, resolveActionState } from '../utils';

import type { RecommendationRow } from './types';
import { raisedAt } from './utils';

/**
 * The state each action's notifications declared, by action.
 *
 * Only `failed` makes this join worth doing: an execution that was attempted and refused leaves
 * the action row neither executed nor ignored, so the row alone reads as "still waiting on you"
 * and nobody is told the attempt was made. The conductor's stream is the only source that says so
 * — until the action row carries it itself, and then this goes.
 *
 * **The latest is chosen by `timestamp`, not by array position.** `useConductorHubNotifications` hands over
 * its event map in insertion order and the production Firestore query is descending, so on the
 * initial snapshot the *oldest* notification about an action arrives last — keeping the last one
 * seen would leave an action the conductor has since republished as `failed` still reading
 * "Awaiting approval". The timestamps are ISO-8601, which orders lexicographically.
 */
const notifiedStates = (notifications: StpNotification[]): Map<string, StpNotification> => {
  const latest = new Map<string, StpNotification>();

  notifications.forEach((notification) => {
    const actionId = getNotifiedActionId(notification);
    if (!actionId) return;

    const held = latest.get(actionId);
    if (!held || held.timestamp.localeCompare(notification.timestamp) <= 0) latest.set(actionId, notification);
  });

  return latest;
};

/**
 * Everything Clive has recommended on the claim, each in its resolved state, in the order it raised
 * them. The panel's status filter decides which of them it shows.
 *
 * Both sources go through the same resolver the log uses, so a card cannot say one thing in one
 * panel and something else in the other.
 */
export const buildRecommendationRows = (actions: AiAction[], notifications: StpNotification[]): RecommendationRow[] => {
  const states = notifiedStates(notifications);

  return actions
    .map((action): RecommendationRow => {
      const latest = states.get(action.action_id);

      return {
        kind: 'recommendation',
        key: activityRowKey('recommendation', action.action_id),
        action,
        state: resolveActionState(action, getNotifiedActionState(latest?.metadata?.action_state)),
        raisedAt: raisedAt(action.created_at),
        logEventId: latest?.event_id,
      };
    })
    .sort((first, second) => first.raisedAt - second.raisedAt);
};
